/* Cart Drawer Custom Element */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.closeButtons = this.querySelectorAll('[data-close]');
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    PubSub.subscribe('cart:open', () => this.open());
    PubSub.subscribe('cart:update', () => this.refresh());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });

    // Intercept the per-line "Remove" link clicks. Without this the
    // anchor's plain href navigates to /cart/change?line=X&quantity=0,
    // which Shopify processes server-side and then 302s the visitor
    // to /cart — kicking them out of whatever page they were on with
    // the drawer open. CartAPI.change posts to /cart/change.js
    // instead and publishes cart:update; the subscribe above then
    // re-runs refresh() which swaps the drawer body's innerHTML
    // for the updated line items in place.
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cart-remove]');
      if (!btn) return;
      // Respect modifier-click so cmd-click etc still opens the
      // /cart fallback in a new tab.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
      e.preventDefault();
      const line = parseInt(btn.getAttribute('data-cart-remove'), 10);
      if (!line) return;
      // Pointer-events lock prevents a double-click firing two
      // change requests before refresh() finishes. CartAPI.change
      // resolves either way (success or error) so we always release.
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.5';
      CartAPI.change(line, 0).catch((err) => {
        console.error('Cart remove failed:', err);
      }).finally(() => {
        // refresh() replaces the button's parent element so this
        // style reset is mostly defensive for the catch path.
        btn.style.pointerEvents = '';
        btn.style.opacity = '';
      });
    });

    // Per-line debounce timers for quantity changes. Spam-clicking
    // the +/- buttons fires a `change` event on the input on every
    // tick; without debouncing we'd POST a request per tick. 350ms
    // is short enough that the visitor doesn't notice a pause but
    // long enough that rapid clicks merge into one request with
    // the final quantity.
    this._qtyDebounce = {};

    // Quantity-picker change → POST to /cart/change.js with the new
    // quantity. The +/- buttons inside <quantity-picker> dispatch
    // `change` on the input after adjusting the value (see
    // QuantityPicker.adjust() in theme.js). The visitor edits the
    // line in place and the drawer refreshes automatically when
    // cart:update fires — no navigation, no manual save button.
    this.addEventListener('change', (e) => {
      const input = e.target.closest('.quantity-picker__input');
      if (!input) return;
      const lineEl = input.closest('[data-line]');
      if (!lineEl) return;
      const line = parseInt(lineEl.getAttribute('data-line'), 10);
      if (!line) return;
      // Coerce to a non-negative integer. 0 effectively removes the
      // line, but the QuantityPicker custom element enforces a
      // min of 1 by default so this branch is mostly defensive.
      const qty = Math.max(0, parseInt(input.value, 10) || 0);

      clearTimeout(this._qtyDebounce[line]);
      this._qtyDebounce[line] = setTimeout(() => {
        delete this._qtyDebounce[line];
        CartAPI.change(line, qty).catch((err) => {
          console.error('Cart qty update failed:', err);
        });
      }, 350);
    });
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    this.refresh();
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
  }

  async refresh() {
    try {
      const html = await fetchHTML(`${window.location.pathname}?section_id=cart-drawer`);
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newBody = doc.getElementById('CartDrawerBody');
      const currentBody = document.getElementById('CartDrawerBody');
      if (newBody && currentBody) {
        currentBody.innerHTML = newBody.innerHTML;
      }

      const newFooter = doc.querySelector('.cart-drawer__footer');
      const currentFooter = this.querySelector('.cart-drawer__footer');
      if (newFooter && currentFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
      } else if (newFooter && !currentFooter) {
        this.querySelector('.cart-drawer__panel').appendChild(newFooter);
      }

      // Overflow auto-correct — if any line carries qty > its per-order cap
      // (data-line-max threaded from product.metafields.custom.per_order_limit),
      // POST a correction. Without this, the overflow sits in the drawer
      // until the user nudges the picker. Each correction publishes
      // cart:update which re-fires refresh, settling in one round-trip
      // since the corrected value is now at the cap.
      this.querySelectorAll('[data-line]').forEach((row) => {
        const line = parseInt(row.getAttribute('data-line'), 10);
        const max = parseInt(row.getAttribute('data-line-max'), 10) || 99;
        const input = row.querySelector('.quantity-picker__input');
        if (!input || !line) return;
        const current = parseInt(input.value, 10) || 0;
        if (current > max) {
          CartAPI.change(line, max).catch((err) => {
            console.error('Cart drawer overflow auto-correct failed:', err);
          });
        }
      });
    } catch (error) {
      console.error('Cart drawer refresh error:', error);
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
